import React, { useState, Fragment } from 'react';
import { EditIcon, TrashIcon, EyeIcon, MailIcon, SendIcon, ClipboardIcon, RefreshCwIcon } from 'lucide-react';
import { EmailTemplate } from './MessagesManagement';
import { StatusBadge } from '../../StatusBadge';
interface EmailTemplatesListProps {
  emailTemplates: EmailTemplate[];
  canEditEmailTemplate: (template: EmailTemplate) => boolean;
  onEdit: (template: EmailTemplate) => void;
  onDelete: (id: string) => void;
  onSendTest: (id: string, email: string) => void;
  locale: string;
}
export function EmailTemplatesList({
  emailTemplates,
  canEditEmailTemplate,
  onEdit,
  onDelete,
  onSendTest,
  locale
}: EmailTemplatesListProps) {
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState<string>('');
  const [showTestEmailInput, setShowTestEmailInput] = useState<string | null>(null);
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      console.log(`Copied ${key} to clipboard`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  const toggleExpand = (id: string) => {
    if (expandedTemplate === id) {
      setExpandedTemplate(null);
    } else {
      setExpandedTemplate(id);
    }
  };
  const toggleTestEmailInput = (id: string) => {
    if (showTestEmailInput === id) {
      setShowTestEmailInput(null);
    } else {
      setShowTestEmailInput(id);
      setTestEmail('');
    }
  };
  const handleSendTest = (id: string) => {
    if (testEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      onSendTest(id, testEmail);
      setShowTestEmailInput(null);
    } else {
      alert('Please enter a valid email address');
    }
  };
  if (emailTemplates.length === 0) {
    return <div className="text-center py-12" data-id="element-18391">
        <MailIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-18392" />
        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-18393">
          No email templates found
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-18394">
          No email templates match your current filters. Try adjusting your
          search criteria.
        </p>
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-18395">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-18396">
        <thead data-id="element-18397">
          <tr data-id="element-18398">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18399">
              Module
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18400">
              Key & Subject
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18401">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18402">
              Usage
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18403">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-18404">
          {emailTemplates.map(template => <Fragment key={template.id} data-id="element-18405">
              <tr className={expandedTemplate === template.id ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-18406">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18407">
                  <div className="flex items-center" data-id="element-18408">
                    <span className="flex-shrink-0" data-id="element-18409">
                      <MailIcon className="h-5 w-5 text-indigo-500" data-id="element-18410" />
                    </span>
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-18411">
                      {template.module}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-18412">
                  <div data-id="element-18413">
                    <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-18414">
                      <button onClick={() => handleCopyKey(template.key)} className="text-gray-500 hover:text-gray-700 mr-1" title="Copy key" data-id="element-18415">
                        <ClipboardIcon className="h-4 w-4" data-id="element-18416" />
                      </button>
                      <span className="font-mono" data-id="element-18417">{template.key}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 line-clamp-2" data-id="element-18418">
                      {template.subject}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-18419">
                  <div className="flex flex-col space-y-1" data-id="element-18420">
                    <StatusBadge status={template.isActive ? 'success' : 'error'} text={template.isActive ? 'Active' : 'Inactive'} data-id="element-18421" />
                    {template.isSystemLocked && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-18422">
                        System Locked
                      </span>}
                    {template.editableByRoles.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-18423">
                        RBAC Limited
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-18424">
                  <div data-id="element-18425">Count: {template.usageCount}</div>
                  <div data-id="element-18426">Last: {formatDate(template.lastUsedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-18427">
                  <div className="flex justify-end space-x-2" data-id="element-18428">
                    <button onClick={() => toggleExpand(template.id)} className="text-indigo-600 hover:text-indigo-900" title="View details" data-id="element-18429">
                      <EyeIcon className="h-5 w-5" data-id="element-18430" />
                    </button>
                    <button onClick={() => toggleTestEmailInput(template.id)} className="text-indigo-600 hover:text-indigo-900" title="Send test email" data-id="element-18431">
                      <SendIcon className="h-5 w-5" data-id="element-18432" />
                    </button>
                    {canEditEmailTemplate(template) && <>
                        <button onClick={() => onEdit(template)} className="text-indigo-600 hover:text-indigo-900" title="Edit template" data-id="element-18433">
                          <EditIcon className="h-5 w-5" data-id="element-18434" />
                        </button>
                        {!template.isSystemLocked && <button onClick={() => onDelete(template.id)} className="text-red-600 hover:text-red-900" title="Delete template" data-id="element-18435">
                            <TrashIcon className="h-5 w-5" data-id="element-18436" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>
              {/* Test email input row */}
              {showTestEmailInput === template.id && <tr className="bg-blue-50" data-id="element-18437">
                  <td colSpan={5} className="px-6 py-4" data-id="element-18438">
                    <div className="flex items-center space-x-3" data-id="element-18439">
                      <label htmlFor="test-email" className="text-sm font-medium text-gray-700" data-id="element-18440">
                        Send test email to:
                      </label>
                      <input type="email" id="test-email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="email@example.com" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-64 sm:text-sm border-gray-300 rounded-md" data-id="element-18441" />
                      <button onClick={() => handleSendTest(template.id)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18442">
                        <SendIcon className="h-4 w-4 mr-2" data-id="element-18443" />
                        Send
                      </button>
                      <button onClick={() => setShowTestEmailInput(null)} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18444">
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>}
              {/* Expanded template details */}
              {expandedTemplate === template.id && <tr className="bg-gray-50" data-id="element-18445">
                  <td colSpan={5} className="px-6 py-4" data-id="element-18446">
                    <div className="space-y-4" data-id="element-18447">
                      <div data-id="element-18448">
                        <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18449">
                          Subject
                        </h4>
                        <div className="p-3 rounded-md text-sm bg-indigo-50 text-indigo-700" data-id="element-18450">
                          {template.subject}
                        </div>
                      </div>
                      <div data-id="element-18451">
                        <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18452">
                          Available Merge Fields
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-18453">
                          {template.mergeFields.map(field => <span key={field} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-18454">
                              {`{{${field}}}`}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-18455">
                        <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18456">
                          Email Preview
                        </h4>
                        <div className="border border-gray-200 rounded-md p-4 bg-white" data-id="element-18457">
                          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                      __html: template.bodyHtml
                    }} data-id="element-18458" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-18459">
                        <div data-id="element-18460">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18461">
                            Created
                          </h4>
                          <div className="text-sm text-gray-500" data-id="element-18462">
                            <div data-id="element-18463">{formatDate(template.createdAt)}</div>
                            <div data-id="element-18464">By: {template.createdBy}</div>
                          </div>
                        </div>
                        {template.modifiedAt && <div data-id="element-18465">
                            <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18466">
                              Last Modified
                            </h4>
                            <div className="text-sm text-gray-500" data-id="element-18467">
                              <div data-id="element-18468">{formatDate(template.modifiedAt)}</div>
                              <div data-id="element-18469">By: {template.modifiedBy}</div>
                            </div>
                          </div>}
                        <div data-id="element-18470">
                          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-18471">
                            RBAC Permissions
                          </h4>
                          {template.editableByRoles.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-18472">
                              {template.editableByRoles.map(role => <span key={role} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-18473">
                                  {role}
                                </span>)}
                            </div> : <div className="text-sm text-gray-500" data-id="element-18474">
                              {template.isSystemLocked ? 'System locked - no editing allowed' : 'No RBAC restrictions'}
                            </div>}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}